<i18n>
{
  "en": {
    "title": "Our Customers Sustainability Report 2017-2018",
    "description": "Our domain expertise fuels our customers’ sustainable results, from green buildings to green energy and beyond. Learn how we partner with customers in four key markets of building, data center, industry, and infrastructure to prioritize sustainable development, reduce carbon footprint, consider renewable resources, and power the digital economy.",
    "ourCustomers": {
      "title": "Our Customers",
      "overview": {
        "quote": {
          "para": "Sustainability is a matter of bottom line and efficiency. So it's a double win, which becomes an obvious imperative for most of the companies throughout the world.",
          "title": "Executive Vice President, Low Voltage — Secure Power, <br>Schneider Electric"
        },
        "vid": {
          "title": "EcoStruxure and Sustainability",
          "description": "Philippe Delorme,<br> Executive Vice President, Low Voltage — Secure Power, <br>Schneider Electric"
        },
        "highlights": {
          "title": "2017 Highlights",
          "para": "Our deep domain expertise fuels our customers’ sustainable results. We partner with customers across segments and around the world to innovate, improve performance, and bring their bold ideas to life. The four key markets that we serve are:",
          "itemA": "Building",
          "itemB": "Data Center",
          "itemC": "Industry",
          "itemD": "Infrastructure",
          "paraB": "Together, these end markets are responsible for 70 percent of the world’s energy consumption<sup>1</sup>. This statistic is staggering, but our measurable customer results over the past decade are promising. Working together affords us groundbreaking opportunity for positive change toward a more sustainable future. We redefine value for our customers through unprecedented connectivity, sustainability, efficiency, reliability, and safety — because we know that the time for working together toward positive change is now, and the need for a sustainable supply chain is more critical than ever. The gap between water supply and demand is projected to be 40% in 2030<sup>2</sup>, and the demand on natural resources and raw materials is also increasing at an alarming rate. According to the United Nations Environment Program (UNEP), the amount of minerals, ores, fossil fuels, and biomass consumed globally per year could triple between today and 2050<sup>3</sup>. That amounts to fewer resources — and more reasons to be resourceful — than ever. Together with our customers, we prioritize resource productivity and management to fuel a circular economy. We promote energy efficiency and rethink energy consumption. We are realistic about the challenges we face, and understand that we are responsible for co-creating smart, sustainable solutions.",
          "paraC1": "At Schneider, we help companies become more efficient in how they buy and consume energy, allowing them to reduce their overall environmental impact without sacrificing efficiency and performance. We are leading the digital transformation of energy management and automation with ",
          "paraCLink": "EcoStruxure",
          "paraC2": "&trade;, our IoT-enabled architecture with connected products; edge control; and apps, analytics, and services.",
          "paraD": "We help customers set targets based on what's right for their brand, their customers, and the environment. We’re empowering our customers and partners, and in turn, powering the digital economy."
        },
        "quoteB": {
          "para": "Companies need to prepare to be an active energy participant, putting the pieces in place to produce energy, and interact with the grid, utilities, peers, and other new entrants. Those that fail to act will be left behind.",
          "title": "Chairman & CEO,"
        },
        "snapshots": {
          "title": "EcoStruxure Highlights",
          "snap1": {
            "title": "Building",
            "one": {
              "stat": "10% to 15%",
              "unit": "energy savings",
              "para": "Marriott hotels, China"
            },
            "two": {
              "stat": "25 to 30%",
              "unit": "reduction in energy costs",
              "para": "University of Mississippi, USA"
            },
            "three": {
              "stat": "$1M",
              "unit": "guaranteed savings over 3.7 years",
              "para": "Cinnamon Grand Hotel, Sri Lanka"
            },
            "four": {
              "stat": "$2M",
              "unit": "avoided in new equipment costs",
              "para": "Pharmacia Corporation, USA"
            }
          },
          "snap2": {
            "title": "Data Center",
            "one": {
              "stat": "60%",
              "unit": "reduction in energy costs",
              "para": "Telefónica, Spain"
            },
            "two": {
              "stat": "15 – 20 kW",
              "unit": "per rack with Hot Aisle containment and InRow<sup>&trade;</sup> cooling",
              "para": "IronGate Data Centers, USA"
            },
            "three": {
              "stat": "~$54K",
              "unit": "per year estimated energy savings",
              "para": "Zhiguli Valley Technopark, Russia"
            },
            "four": {
              "stat": "3-year",
              "unit": "projected ROI on $1.5M ",
              "para": "ITS Telecommunications Systems, USA"
            }
          },
          "snap3": {
            "title": "Industry",
            "one": {
              "stat": "Up to 50%",
              "unit": "energy savings reported by farmers",
              "para": "Waterforce, New Zealand"
            },
            "two": {
              "stat": "50%",
              "unit": "reduction in CapEx",
              "para": "Dow Corning, USA"
            },
            "three": {
              "stat": "24/7",
              "unit": "nonstop steel production",
              "para": "ArcelorMittal, France"
            },
            "four": {
              "stat": "4.2 to 4.4 million",
              "unit": "liters of milk per day",
              "para": "Fonterra, New Zealand"
            }
          },
          "snap4": {
            "title": "Infrastructure",
            "one": {
              "stat": "1.5 million",
              "unit": "homes receiving reliable electricity",
              "para": "South Australia Power Networks"
            },
            "two": {
              "stat": "144 GWh",
              "unit": "energy savings per year",
              "para": "Enel, Italy"
            },
            "three": {
              "stat": "44%",
              "unit": "reduction of configured alarms",
              "para": "Salt River Project, USA"
            },
            "four": {
              "stat": "&lt;20 seconds",
              "unit": "average power restoration time",
              "para": "Tata Power, India"
            }
          }
        },
        "txt1": {
          "title": "2017 &ndash; 2018 Recognitions",
          "p1Bold": "Leader in Green Quadrant<sup>&reg;</sup> Facility Optimization Software report",
          "p1Reg": "Comparing 14 facility optimization software applications by independent analyst Verdantix",
          "p2Bold": "Ranked in Gartner<sup>&reg;</sup> 2017 Supply Chain Top 15 for Europe",
          "p2Reg": "Number 7 supply chain, ranking among the top 10 for third consecutive year",
          "p3Bold": "Ranked in Gartner’s Supply Chain Top 25 for 2017",
          "p3Reg": "Number 17 supply chain, advancing one place from 2016 rankings",
          "p4Bold": "European regional finalist, Most Admired Knowledge Enterprises (MAKE) research program",
          "p4Reg": "Selected from a list of 48 nominated companies in various sectors",
          "p5Bold": "Engineers’ Choice for best Motion Control",
          "p5Reg": "Altivar Process 900 honored by Control Engineering for optimizing performance across sectors",
          "p6Bold": "&quot;Customer Innovation of the Year&quot;",
          "p6Reg": "Supply Chain Breakthrough award in SCM World’s 2018 Power of the Profession Awards",
          "p7Bold": "&quot;Integrated Thinking&quot; award",
          "p7Reg": "By the Institut du Capitalisme Responsable [Responsible Capitalism Institute] in the &quot;large companies&quot; category",
          "p8Bold": "Leader in Gartner’s Magic Quadrant for Advanced Distribution Management Systems",
          "p8Reg": "Highest scores in all three use cases in the Critical Capabilities for fifth consecutive year"
        },
        "references": {
          "one": "<sup>1</sup> Source: World Energy Outlook 2012, OECD/IEA, Internal analysis",
          "two": "<sup>2</sup> Source: The Water Resources Group, World Economic Forum Annual Meeting, 2012",
          "three": "<sup>3</sup> Source: National Intelligence Council Report, July 2013"
        }
      }
    }
  },
  "fr": {
    "title": "Our Customers Sustainability Report 2017-2018",
    "description": "Our domain expertise fuels our customers’ sustainable results, from green buildings to green energy and beyond. Learn how we partner with customers in four key markets of building, data center, industry, and infrastructure to prioritize sustainable development, reduce carbon footprint, consider renewable resources, and power the digital economy.",
    "ourCustomers": {
      "title": "Nos Clients",
      "overview": {
        "quote": {
          "para": "Le développement durable est une question de résultats et d'efficacité. C'est donc une double victoire qui devient un impératif pour la plupart des entreprises dans le monde.",
          "title": "Directeur général Basse Tension & Énergie sécurisée, <br>Schneider Electric"
        },
        "vid": {
          "title": "Ecostruxure et le développement durable",
          "description": "Philippe Delorme,<br> Directeur général Basse Tension & Énergie sécurisée, <br>Schneider Electric"
        },
        "highlights": {
          "title": "Faits marquants 2017",
          "para": "Notre expertise approfondie du domaine contribue aux résultats durables de nos clients. Partout dans le monde et dans divers secteurs d'activité, nous nouons des partenariats avec nos clients pour innover, améliorer leur performance et donner vie à leurs idées audacieuses. Schneider Electric sert ses clients sur quatre grands marchés :",
          "itemA": "Bâtiments ;",
          "itemB": "Centres de données ;",
          "itemC": "Industries ;",
          "itemD": "Infrastructures.",
          "paraB": "Ces quatre marchés réunis représentent 70 % de la consommation d’énergie mondiale <sup>1</sup>. Certes, ce chiffre donne le tournis, mais nous avons obtenu ces dix dernières années des résultats quantifiables et prometteurs. Les opportunités de collaboration nous donnent la possibilité inédite d’œuvrer en faveur d’un changement positif et d'un avenir plus durable. Nous redéfinissons la valeur au bénéfice de nos clients en leur proposant toujours plus de connectivité, de durabilité, d’efficacité, de fiabilité et de sécurité &mdash; car nous savons que l'heure est venue de travailler main dans la main pour initier un changement positif, et que le déploiement d’une chaîne logistique durable est plus que jamais nécessaire. L'écart entre l’offre et la demande d’eau devrait atteindre 40 % en 2030 <sup>2</sup>, et la demande de ressources naturelles et de matières premières croît elle aussi à une vitesse alarmante. Selon le Programme des Nations unies pour l’environnement (PNUE), la quantité de minéraux, de minerais, de combustibles fossiles et de biomasse consommée annuellement dans le monde pourrait tripler d’ici 2050 <sup>3</sup>, d’où une raréfaction toujours plus importante des ressources &mdash; raison de plus pour nous montrer imaginatifs. Avec nos clients, nous mettons la priorité sur la gestion et la productivité des ressources dans une optique d’économie circulaire. Nous promouvons l’efficacité énergétique et repensons la consommation d'énergie. Nous sommes réalistes face aux défis qui nous attendent, et savons que notre devoir est de co-créer des solutions intelligentes et durables.",
          "paraC1": "Chez Schneider Electric, nous aidons les entreprises à gagner en efficacité dans leur achat et leur consommation d'énergie, en leur permettant de réduire leur impact environnemental global sans perdre en efficacité ni en performance. Nous menons la transformation digitale de la gestion de l’énergie et des automatismes avec ",
          "paraCLink": "EcoStruxure",
          "paraC2": "&trade;, notre architecture IdO pour les produits connectés, le contrôle local, et les applications, outils d’analyse et services.",
          "paraD": "Nous aidons nos clients à se fixer des objectifs en fonction des avantages qu’ils veulent en retirer pour leur marque, pour leurs clients et pour l’environnement. Nous donnons les clés de la réussite à nos clients et partenaires, et ce faisant, nous assurons le déploiement de  l’économie numérique."
        },
        "quoteB": {
          "para": "Les entreprises doivent se préparer à participer activement au marché de l’énergie en assemblant les différentes pièces du puzzle pour produire de l'énergie et interagir avec le réseau, les fournisseurs d'énergie, les concurrents et autres nouveaux entrants. Celles qui n'agissent pas seront vite dépassées.",
          "title": "Préisdent Directeur général,"
        },
        "snapshots": {
          "title": "Faits marquants EcoStruxure",
          "snap1": {
            "title": "Bâtiments",
            "one": {
              "stat": "10 à 15 %",
              "unit": "d'économies d'énergie",
              "para": "Marriott hotels, Chine"
            },
            "two": {
              "stat": "25 à 30 %",
              "unit": "de réduction des coûts de l'énergie",
              "para": "Université du Mississipi, Etats -Unis"
            },
            "three": {
              "stat": "1 million de dollars",
              "unit": "économies garanties sur 3,7 années",
              "para": "Cinamon Grand Hôtel, Sri Lanka"
            },
            "four": {
              "stat": "2 millions de dollars",
              "unit": "économisés dans les coûts de nouveaux équipements",
              "para": "Pharmacia Corportion, USA"
            }
          },
          "snap2": {
            "title": "Centres de données",
            "one": {
              "stat": "60 %",
              "unit": "de réduction des coûts d'énergie",
              "para": "Telefonica, Espagne"
            },
            "two": {
              "stat": "15 – 20 kW",
              "unit": "par rack avec le système de refroidissement Hot Aisle containement and InRow<sup>TM</sup>",
              "para": "Centres de données IronGate, Etats-Unis"
            },
            "three": {
              "stat": "Environ 54K dollars",
              "unit": "d'économies d'énergies estimées par an",
              "para": "Zhiguli Valley Technopark, Russie"
            },
            "four": {
              "stat": "3 ans",
              "unit": "ROI prévu sur 1,5 million de dollars",
              "para": "ITS Telecommunicaitons System, Etats-Unis"
            }
          },
          "snap3": {
            "title": "Industries",
            "one": {
              "stat": "Jusqu'à 50 %",
              "unit": "d'économies d'énergie constatées par les agriculteurs",
              "para": "Waterforce, Nouvelle Zélande"
            },
            "two": {
              "stat": "50 %",
              "unit": "de réduction du CAPEX",
              "para": "Down Corning, Etats-Unis"
            },
            "three": {
              "stat": "24 heures sur 24",
              "unit": "production d'acier non stop",
              "para": "ArcelorMittal, France"
            },
            "four": {
              "stat": "4,2 à 4,4 millions",
              "unit": "de litres de lait par jour",
              "para": "Fonterra, Nouvelle Zélande "
            }
          },
          "snap4": {
            "title": "Infrastructures",
            "one": {
              "stat": "1,5 million",
              "unit": "de maisons ayant accès à une électricité fiable",
              "para": "South Australia Power Networks"
            },
            "two": {
              "stat": "144 GWh",
              "unit": "économies d'énergie annuelles",
              "para": "Enel, Italie"
            },
            "three": {
              "stat": "44 %",
              "unit": "réduction des alarmes configurées",
              "para": "Salt River Projects, Etats-Unis"
            },
            "four": {
              "stat": "&lt; à 20 secondes",
              "unit": "la moyenne du temps de restauration de la puissance ",
              "para": "Tata Power, India"
            }
          }
        },
        "txt1": {
          "title": "Distinctions 2017 &ndash; 2018",
          "p1Bold": "Désigné Leader dans le rapport Green Quadrant des logiciels d'optimisation d'installation",
          "p1Reg": "Comparaison de 14 logiciels d'optimatisation d'installation par un analyste indépendant Verdantix",
          "p2Bold": "Présent dans le classement Gartner des 15 chaînes logistiques européennes",
          "p2Reg": "Désigné 7ème chaîne logistique parmi le TOP 10 pour la troisième année consécutive",
          "p3Bold": "Présent dans le classement Gartner des TOP 25 chaînes logistiques en 2017",
          "p3Reg": "Désigné 17ème chaîne logistique, une place gagnée par rapport aux classement 2016",
          "p4Bold": "Finaliste européen dans le programme de recherche  Most admired Knowledge Entreprises (MAKE)",
          "p4Reg": "Sélectionné dans plusieurs secteurs  parmi une liste de 48 entreprises nominées ",
          "p5Bold": "Sélectionné par les ingénieurs pour la meilleur commande de mouvement",
          "p5Reg": "Notre variateur de vitesse Altivar Process 900 honoré par Control Engineering pour ses capacités à optimiser la performance dans différents secteurs",
          "p6Bold": "&ldquo;Innovation client de l'année&rdquo;",
          "p6Reg": "Notre chaîne logistique récompensée dans SMC World's 2018 Power of the Profession Awards",
          "p7Bold": "&ldquo;Integrated Thinking awards&rdquo;",
          "p7Reg": "Organisé par l'Institut du Capitalisme  Reponsable et lauréat dans la catégorie Grandes Entreprises ",
          "p8Bold": "Leader dans le classement Gartner Magic Quadrant pour nos systèmes avancés de gestion de la distribution électrique",
          "p8Reg": "Les scores les plus hauts dans les trois cas d'utilisation  de capacités critiques pour la cinquième année consécutive"
        },
        "references": {
          "one": "<sup>1</sup> Source: World Energy Outlook 2012, OCDE/AIE, Analyse interne Schneider Electric",
          "two": "<sup>2</sup> Source: The Water Resources Group, réunion annuelle du Forum économique mondial, 2012",
          "three": "<sup>3</sup> Source: National Intelligence Council Report, juillet 2013"
        }
      }
    }
  }
}
</i18n>
<template>
  <div id="">
    <Hero :loResSrc="require('../../assets/img/homepage/our-customers-low.jpg')" :hiResSrc="require('../../assets/img/homepage/our-customers.jpg')" chapter="03" :title="$t('ourCustomers.title')" :grayBG="true" />

    <section class="m-b-120">
      <Quote :photo="require('../../assets/img/our-customers/Philippe-Delorme_2017-18.jpg')" name="Philippe Delorme" :title="$t('ourCustomers.overview.quote.title')">
        <div v-html="$t('ourCustomers.overview.quote.para')"></div>
      </Quote>
    </section>

    <section class="m-b-120 m-t-120">
      <YouTubeVideo videoId="uWyz4Sqd7LY" :title="$t('ourCustomers.overview.vid.title')" :description="$t('ourCustomers.overview.vid.description')" :preview="require('@/assets/img/thumbnails/SSDHighlightVideo_PhilippeDelorme_Still.jpg')" />
    </section>

    <section id="2017-highlights" class="m-b-120 m-t-120">
      <Txt>
        <h2 id="customerhighlights" v-html="$t('ourCustomers.overview.highlights.title')"/>
        <p v-html="$t('ourCustomers.overview.highlights.para')"/>
        <ul>
          <li v-html="$t('ourCustomers.overview.highlights.itemA')" />
          <li v-html="$t('ourCustomers.overview.highlights.itemB')" />
          <li v-html="$t('ourCustomers.overview.highlights.itemC')" />
          <li v-html="$t('ourCustomers.overview.highlights.itemD')" />
        </ul>
        <p v-html="$t('ourCustomers.overview.highlights.paraB')"/>
        <p>
          <span v-html="$t('ourCustomers.overview.highlights.paraC1')"></span><span><router-link v-html="$t('ourCustomers.overview.highlights.paraCLink')" :to="{name: 'SustainableSolutions_Ecostruxure'}"></router-link></span><span v-html="$t('ourCustomers.overview.highlights.paraC2')"></span>
        </p>
        <p v-html="$t('ourCustomers.overview.highlights.paraD')"/>
      </Txt>
    </section>

    <section class="m-b-50 m-t-120">
      <Quote :photo="require('../../assets/img/our-customers/introduction-welcome-quote-2.jpg')" name="Jean-Pascal Tricoire" :title="$t('ourCustomers.overview.quoteB.title')" company="Schneider Electric">
        <div v-html="$t('ourCustomers.overview.quoteB.para')"></div>
      </Quote>
    </section>

    <section class="m-b-120 m-t-120">
      <Snapshot class="m-b-50">
        <div slot="above">
          <h2 id="2017ecostruxurehighlights" v-html="$t('ourCustomers.overview.snapshots.title')"></h2>
          <h3 v-html="$t('ourCustomers.overview.snapshots.snap1.title')"></h3>
        </div>
        <SnapshotCard :double="true" class="t-white" bgc="#3dcd58">
          <h4 class="t-white" v-html="$t('ourCustomers.overview.snapshots.snap1.one.stat')"></h4>
          <div class="t-hr t-hr--white"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap1.one.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap1.one.para')"/>
        </SnapshotCard>
        <SnapshotCard class="t-white" :double="true" :bgimg="require('@/assets/img/our-customers/42-20584830_RGB_EA.jpg')">
          <h4 class="" v-html="$t('ourCustomers.overview.snapshots.snap1.two.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap1.two.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap1.two.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true">
          <h4 class="t-green-light" v-html="$t('ourCustomers.overview.snapshots.snap1.three.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap1.three.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap1.three.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true" class="t-white" :bgimg="require('@/assets/img/our-customers/GettyImages-485206425.jpg')">
          <h4 class="" v-html="$t('ourCustomers.overview.snapshots.snap1.four.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap1.four.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap1.four.para')"/>
        </SnapshotCard>
      </Snapshot>
      <Snapshot class="m-b-50">
        <div slot="above">
          <h3 v-html="$t('ourCustomers.overview.snapshots.snap2.title')"></h3>
        </div>
        <SnapshotCard :double="true" class="t-white" :bgimg="require('@/assets/img/our-customers/Telefonica_MG_3331-web.jpg')">
          <h4 class="" v-html="$t('ourCustomers.overview.snapshots.snap2.one.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap2.one.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap2.one.para')"/>
        </SnapshotCard>
        <SnapshotCard class="t-white" :double="true" bgc="#626469">
          <h4 class="t-white" v-html="$t('ourCustomers.overview.snapshots.snap2.two.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap2.two.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap2.two.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true" class="t-white" bgc="#3dcd58">
          <h4 class="t-white" v-html="$t('ourCustomers.overview.snapshots.snap2.three.stat')"></h4>
          <div class="t-hr t-hr--white"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap2.three.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap2.three.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true" class="t-white" :bgimg="require('@/assets/img/our-customers/42-20284540.jpg')">
          <h4 class="" v-html="$t('ourCustomers.overview.snapshots.snap2.four.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap2.four.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap2.four.para')"/>
        </SnapshotCard>
      </Snapshot>
      <Snapshot class="m-b-50">
        <div slot="above">
          <h3 v-html="$t('ourCustomers.overview.snapshots.snap3.title')"></h3>
        </div>
        <SnapshotCard :double="true" class="t-white" :bgimg="require('@/assets/img/our-customers/227_1920_572.jpg')">
          <h4 class="" v-html="$t('ourCustomers.overview.snapshots.snap3.one.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap3.one.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap3.one.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true" class="t-white" bgc="#3dcd58">
          <h4 class="t-whitet" v-html="$t('ourCustomers.overview.snapshots.snap3.two.stat')"></h4>
          <div class="t-hr t-hr--white"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap3.two.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap3.two.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true" class="t-white" :bgimg="require('@/assets/img/our-customers/ArcelorMittal_G9A3017.jpg')">
          <h4 class="" v-html="$t('ourCustomers.overview.snapshots.snap3.three.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap3.three.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap3.three.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true">
          <h4 class="t-green-light" v-html="$t('ourCustomers.overview.snapshots.snap3.four.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap3.four.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap3.four.para')"/>
        </SnapshotCard>
      </Snapshot>
      <Snapshot class="m-b-50">
        <div slot="above">
          <h3 v-html="$t('ourCustomers.overview.snapshots.snap4.title')"></h3>
        </div>
        <SnapshotCard :double="true">
          <h4 class="t-green-light" v-html="$t('ourCustomers.overview.snapshots.snap4.one.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap4.one.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap4.one.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true" class="t-white" :bgimg="require('@/assets/img/our-customers/Enel_DSC_5194.jpg')">
          <h4 class="" v-html="$t('ourCustomers.overview.snapshots.snap4.two.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap4.two.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap4.two.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true" class="t-white" :bgimg="require('@/assets/img/our-customers/CUP0048999_rgb.jpg')">
          <h4 class="" v-html="$t('ourCustomers.overview.snapshots.snap4.three.stat')"></h4>
          <div class="t-hr"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap4.three.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap4.three.para')"/>
        </SnapshotCard>
        <SnapshotCard :double="true" class="t-white" bgc="#3dcd58">
          <h4 class="t-white" v-html="$t('ourCustomers.overview.snapshots.snap4.four.stat')"></h4>
          <div class="t-hr t-hr--white"></div>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap4.four.unit')"/>
          <p class="t-chartbody" v-html="$t('ourCustomers.overview.snapshots.snap4.four.para')"/>
        </SnapshotCard>
      </Snapshot>
    </section>

    <section class="l-bg-white l-section-pad m-b-50">
      <Txt>
        <h2 id="customerrecognitions" v-html="$t('ourCustomers.overview.txt1.title')"></h2>
        <p class="m-b-0"><strong v-html="$t('ourCustomers.overview.txt1.p1Bold')"></strong></p>
        <p v-html="$t('ourCustomers.overview.txt1.p1Reg')"></p>
        <p class="m-b-0"><strong v-html="$t('ourCustomers.overview.txt1.p2Bold')"></strong></p>
        <p v-html="$t('ourCustomers.overview.txt1.p2Reg')"></p>
        <p class="m-b-0"><strong v-html="$t('ourCustomers.overview.txt1.p3Bold')"></strong></p>
        <p v-html="$t('ourCustomers.overview.txt1.p3Reg')"></p>
        <p class="m-b-0"><strong v-html="$t('ourCustomers.overview.txt1.p4Bold')"></strong></p>
        <p v-html="$t('ourCustomers.overview.txt1.p4Reg')"></p>
        <p class="m-b-0"><strong v-html="$t('ourCustomers.overview.txt1.p5Bold')"></strong></p>
        <p v-html="$t('ourCustomers.overview.txt1.p5Reg')"></p>
        <p class="m-b-0"><strong v-html="$t('ourCustomers.overview.txt1.p6Bold')"></strong></p>
        <p v-html="$t('ourCustomers.overview.txt1.p6Reg')"></p>
        <p class="m-b-0"><strong v-html="$t('ourCustomers.overview.txt1.p7Bold')"></strong></p>
        <p v-html="$t('ourCustomers.overview.txt1.p7Reg')"></p>
        <p class="m-b-0"><strong v-html="$t('ourCustomers.overview.txt1.p8Bold')"></strong></p>
        <p v-html="$t('ourCustomers.overview.txt1.p8Reg')"></p>
      </Txt>
    </section>

    <References class="m-b-30">
      <Reference v-html="$t('ourCustomers.overview.references.one')"/>
      <Reference v-html="$t('ourCustomers.overview.references.two')"/>
      <Reference v-html="$t('ourCustomers.overview.references.three')"/>
    </References>

   <div class="l-content">
      <FooterNav
        nextRoute="OurCustomers_Building"
        prevRoute="SustainableSolutions_SupplyChain" />
    </div>

  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import Quote from '@/components/Quote'
import Statement from '@/components/Statement'
import FourCols from '@/components/FourCols'
import FourColsItem from '@/components/FourColsItem'
import CTA from '@/components/CTA'
import Txt from '@/components/Txt'
import References from '@/components/References'
import Reference from '@/components/Reference'
import YouTubeVideo from '@/components/YouTubeVideo'
import Snapshot from '@/components/Snapshot'
import SnapshotCard from '@/components/SnapshotCard'

export default {
  name: 'Introduction_Welcome',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    Quote,
    Statement,
    FourCols,
    FourColsItem,
    CTA,
    Txt,
    References,
    Reference,
    YouTubeVideo,
    Snapshot,
    SnapshotCard
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/our-customers/overview.vue